import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { FeatureFlagService } from '@services/helpers/feature-flag.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { Subject, filter, takeUntil } from 'rxjs';
import { AI_ASSISTANT_FEATURE_FLAG, AI_ASSISTANT_VIEW_PERMISSIONS } from '../../models/ai-assistant.models';
import { AiAssistantStateService } from '../../services/ai-assistant-state.service';
import { AiAssistantPanelComponent } from '../ai-assistant-panel/ai-assistant-panel.component';

/**
 * Global floating launcher for the assistant, mounted once in the app shell.
 *
 * Visible only when the `ai_assistant_leads` feature flag is on AND the user holds a
 * lead view/manage permission. Hidden on the internal chat page (two chat launchers
 * would be confusing there). On narrow screens it steps aside while a CRM right-bar is
 * open so the two don't overlap (plan risk mitigation).
 */
@Component({
  selector: 'app-ai-assistant-fab',
  standalone: true,
  imports: [CommonModule, AiAssistantPanelComponent],
  templateUrl: './ai-assistant-fab.component.html',
  styleUrls: ['./ai-assistant-fab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantFabComponent implements OnInit, OnDestroy {
  enabled = false;
  isMobile = false;

  readonly isOpen$ = this.state.isOpen$;

  private readonly destroy$ = new Subject<void>();

  private onChatPage = false;

  constructor(
    private readonly state: AiAssistantStateService,
    private readonly featureFlags: FeatureFlagService,
    private readonly permissions: NgxPermissionsService,
    private readonly lmService: LeadManagementService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateViewport();
    this.updateOnChatPage(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        this.updateOnChatPage(event.urlAfterRedirects);
        this.cdr.markForCheck();
      });

    if (!this.featureFlags.isFeatureEnabled(AI_ASSISTANT_FEATURE_FLAG)) {
      this.enabled = false;
      return;
    }
    this.evaluatePermission();
    // Permissions can load/refresh after this component mounts (e.g. office switch).
    this.permissions.permissions$.pipe(takeUntil(this.destroy$)).subscribe(() => this.evaluatePermission());
  }

  /** Hidden on the internal chat page so the two chat surfaces don't overlap. */
  get hiddenOnChatPage(): boolean {
    return this.onChatPage;
  }

  /** On narrow screens, yield to an open CRM right-bar to avoid overlap. */
  get hiddenByRightBar(): boolean {
    return this.isMobile && !!this.lmService.showRightBar;
  }

  toggle(): void {
    this.state.toggle();
  }

  @HostListener('window:resize')
  onResize(): void {
    const wasMobile = this.isMobile;
    this.updateViewport();
    if (wasMobile !== this.isMobile) {
      this.cdr.markForCheck();
    }
  }

  private updateViewport(): void {
    this.isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  }

  private updateOnChatPage(url: string): void {
    this.onChatPage = url.split(/[?#]/)[0].split('/').includes('chat');
  }

  private async evaluatePermission(): Promise<void> {
    try {
      const allowed = await this.permissions.hasPermission(AI_ASSISTANT_VIEW_PERMISSIONS);
      this.enabled = allowed;
    } catch {
      this.enabled = false;
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
